import { Injectable } from '@angular/core';
import { SortDirection } from '@angular/material/sort';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { GradingScheme } from 'app/library/grading-scheme/models/grading-scheme.model';
import { GradingSchemeState } from 'app/library/grading-scheme/models/grading-scheme.state';
import { AddPartialGrades } from 'app/library/partial-grade/actions';
import { PartialGrade } from 'app/library/partial-grade/models/partial-grade.model';
import { PartialGradeState } from 'app/library/partial-grade/models/partial-grade.state';
import { SubentityState } from 'app/projects/subentity/src/lib/models/subentity.state';
import { User } from 'app/projects/user/src/lib/models/user';
import { UserState } from 'app/projects/user/src/lib/models/user.state';
import { map } from 'rxjs/operators';
import {
    DeselectSelectedGradedUnitPartialGrades,
    FetchGradedUnitPartialGrades,
    FetchGradedUnitPartialGradesFailure,
    FetchGradedUnitPartialGradesSuccess,
    SearchGradedUnitPartialGrades,
    SelectFilteredGradedUnitPartialGrades,
    SetGradedUnitPartialGrades,
    SortGradedUnitPartialGrades,
    ToggleSelectedGradedUnitPartialGrade,
    UnsetGradedUnitPartialGrades,
} from '../actions';
import { GradedUnitPartialGradesService } from '../services/graded-unit-partial-grades.service';
import { GradedUnitPartialGradesStateModel } from './graded-unit-partial-grades.state-model';

@State<GradedUnitPartialGradesStateModel>({
    name: 'gradedUnitPartialGrades',
    defaults: new GradedUnitPartialGradesStateModel(),
})
@Injectable()
export class GradedUnitPartialGradesState extends SubentityState<PartialGrade> {
    @Selector([PartialGradeState.getEntitiesAsMap, GradingSchemeState.getEntity])
    static getEntities(state: GradedUnitPartialGradesStateModel, partialGrades: Map<string, PartialGrade>, gradingScheme: GradingScheme): PartialGrade[] {
        if (!partialGrades) {
            return null;
        }
        const toReturn = state.list.map((id) => partialGrades.get(id));
        if (gradingScheme) {
            const gradesContainer = {};
            gradingScheme.grades.forEach((grade) => {
                gradesContainer[grade.id] = grade;
            });
            return toReturn.map<PartialGrade>((partialGrade) =>
                Object.assign<PartialGrade, PartialGrade, Partial<PartialGrade>>(new PartialGrade(), partialGrade, { grade: gradesContainer[partialGrade.gradeId] || null })
            );
        }
        return toReturn;
    }

    @Selector([PartialGradeState.getEntitiesAsMap, GradingSchemeState.getEntity, UserState.getEntities])
    static getEntitiesWithUser(state: GradedUnitPartialGradesStateModel, partialGrades: Map<string, PartialGrade>, gradingScheme: GradingScheme, users: User[]): PartialGrade[] {
        const toReturn = GradedUnitPartialGradesState.getEntities(state, partialGrades, gradingScheme);

        if (toReturn && users) {
            const usersContainer = {};

            users.forEach((user) => {
                usersContainer[user.id] = user;
            });

            return toReturn.map<PartialGrade>((partialGrade) => {
                return partialGrade;
            });
        }

        return toReturn;
    }

    @Selector([PartialGradeState.getEntitiesAsMap, GradingSchemeState.getEntity])
    static getFilteredEntities(state: GradedUnitPartialGradesStateModel, partialGrades: Map<string, PartialGrade>, gradingScheme: GradingScheme): PartialGrade[] {
        if (!partialGrades) {
            return null;
        }
        const toReturn = state.filtered.map((id) => partialGrades.get(id));
        if (gradingScheme) {
            const gradesContainer = {};
            gradingScheme.grades.forEach((grade) => {
                gradesContainer[grade.id] = grade;
            });
            return toReturn.map<PartialGrade>((partialGrade) =>
                Object.assign<PartialGrade, PartialGrade, Partial<PartialGrade>>(new PartialGrade(), partialGrade, { grade: gradesContainer[partialGrade.gradeId] || null })
            );
        }
        return toReturn;
    }

    @Selector([PartialGradeState.getEntities, GradingSchemeState.getEntity, UserState.getEntities])
    static getFilteredEntitiesWithUser(
        state: GradedUnitPartialGradesStateModel,
        partialGrades: Map<string, PartialGrade>,
        gradingScheme: GradingScheme,
        users: User[]
    ): PartialGrade[] {
        const toReturn = GradedUnitPartialGradesState.getFilteredEntities(state, partialGrades, gradingScheme);

        if (toReturn && users) {
            const usersContainer = {};

            users.forEach((user) => {
                usersContainer[user.id] = user;
            });

            return toReturn.map<PartialGrade>((partialGrade) =>
                Object.assign<PartialGrade, PartialGrade, Partial<PartialGrade>>(new PartialGrade(), partialGrade, { user: usersContainer[partialGrade.participantId] || null })
            );
        }

        return toReturn;
    }

    @Selector()
    static getSelectedEntityIds(state: GradedUnitPartialGradesStateModel): string[] {
        return state.selected;
    }

    @Selector()
    static getFiltersList(state: GradedUnitPartialGradesStateModel): string[] {
        return state.filters.list;
    }

    @Selector()
    static getSearchTerm(state: GradedUnitPartialGradesStateModel): string {
        return state.searchTerm;
    }

    @Selector()
    static getTotalCount(state: GradedUnitPartialGradesStateModel): number {
        return state.totalCount;
    }

    @Selector()
    static getSortBy(state: GradedUnitPartialGradesStateModel): string {
        return state.sortBy;
    }

    @Selector()
    static getSortOrder(state: GradedUnitPartialGradesStateModel): SortDirection {
        return state.sortOrder;
    }

    @Selector()
    static getPageSize(state: GradedUnitPartialGradesStateModel): number {
        return state.pageSize;
    }

    @Selector()
    static getPageNumber(state: GradedUnitPartialGradesStateModel): number {
        return state.pageNumber || 1;
    }

    @Selector()
    static getPageIndex(state: GradedUnitPartialGradesStateModel): number {
        return state.pageNumber ? state.pageNumber - 1 : 0;
    }

    constructor(private _gradedUnitPartialGradesService: GradedUnitPartialGradesService, private _store: Store) {
        super(null, 30);

        this._propstoSearch = [...PartialGrade.props_to_search];
    }

    @Action(SetGradedUnitPartialGrades)
    setAll(ctx: StateContext<GradedUnitPartialGradesStateModel>, action: SetGradedUnitPartialGrades): void {
        this._setSubentities(ctx, action, AddPartialGrades);
    }

    @Action(UnsetGradedUnitPartialGrades)
    unsetAll(ctx: StateContext<GradedUnitPartialGradesStateModel>): void {
        this._unsetSubentities(ctx);
    }

    @Action(FetchGradedUnitPartialGrades)
    fetchAll(ctx: StateContext<GradedUnitPartialGradesStateModel>, action: FetchGradedUnitPartialGrades): void {
        this._fetchSubentities(
            ctx,
            { payload: 'all' },
            SetGradedUnitPartialGrades,
            FetchGradedUnitPartialGradesSuccess,
            FetchGradedUnitPartialGradesFailure,
            this._gradedUnitPartialGradesService
                .findGrades(action.payload.gradedUnit, action.payload.search, action.payload.sortBy, action.payload.sortOrder, action.payload.pageSize, action.payload.pageNumber)
                .pipe(
                    map((data) => {
                        ctx.patchState({
                            totalCount: data.pagination.number_of_results,
                        });

                        return data.data.map((query) => new PartialGrade(query));
                    })
                )
        );
    }

    @Action(FetchGradedUnitPartialGradesSuccess)
    fetchAllSuccess(ctx: StateContext<GradedUnitPartialGradesStateModel>): void {
        this._fetchSubentitiesSuccess(ctx);
    }

    @Action(FetchGradedUnitPartialGradesFailure)
    fetchAllFailure(ctx: StateContext<GradedUnitPartialGradesStateModel>): void {
        this._fetchSubentitiesFailure(ctx);
    }

    @Action(ToggleSelectedGradedUnitPartialGrade)
    toggleSelect(ctx: StateContext<GradedUnitPartialGradesStateModel>, action: ToggleSelectedGradedUnitPartialGrade): void {
        this._toggleSelectedSubentity(ctx, action);
    }

    @Action(SelectFilteredGradedUnitPartialGrades)
    selectAll(ctx: StateContext<GradedUnitPartialGradesStateModel>): void {
        this._selectFilteredSubentities(ctx);
    }

    @Action(DeselectSelectedGradedUnitPartialGrades)
    deselectAll(ctx: StateContext<GradedUnitPartialGradesStateModel>): void {
        this._deselectSelectedSubentities(ctx);
    }

    @Action(SearchGradedUnitPartialGrades)
    search({ patchState }: StateContext<GradedUnitPartialGradesStateModel>, { payload }: SearchGradedUnitPartialGrades): void {
        sessionStorage.setItem('gradedUnitPartialGradesSearchTerm', payload);
        patchState({
            searchTerm: payload,
        });
    }

    @Action(SortGradedUnitPartialGrades)
    sort({ patchState }: StateContext<GradedUnitPartialGradesStateModel>, { payload }: SortGradedUnitPartialGrades): void {
        sessionStorage.setItem('gradedUnitPartialGradesSortBy', payload.active);
        sessionStorage.setItem('gradedUnitPartialGradesSortOrder', payload.direction);

        patchState({
            sortBy: payload.active,
            sortOrder: payload.direction,
        });
    }
}
